import React, { useState } from 'react';
import Layout from './Layout';
import { motion } from 'framer-motion';
import { FaCopy } from 'react-icons/fa';

const AArticleAutoVarianceAnalysis = () => {
  const [copiedStates, setCopiedStates] = useState({});

  const sections = [
    {
      id: 1,
      title: "Why Do We Need ANOVA in Freight Forwarding?",
      text: `Imagine you're managing delivery times across five different offices. You want to know if some offices are faster or slower than others to imrpove speed and costs which are the two key variables in logistics.  
You could compare them two at a time, but that would take too long and might give wrong results.

ANOVA (Analysis of variance) allows us to compare all offices at once, and tells us if there are real differences between them. It's like having a radar that can spot which delivery routes need attention due to underperformance.`,
      explanation: "Understanding why we use ANOVA for comparing multiple groups"
    },
    {
      id: 2,
      title: "Setting Up Your Analysis",
      text: `Let's look at a real example: comparing delivery times between different regional offices by generating some fake example data using python:`,
      code: `import numpy as np
import pandas as pd
import scipy.stats as stats

# Create example data for 5 offices
offices = ["North", "South", "East", "West", "Central"]
data = pd.DataFrame({
    "office": np.repeat(offices, 200),
    "delivery_time": np.random.normal(24, 5, 1000)  # 24 hours average delivery
})

print("First few rows of our data:")
print(data.head())`,
      output: `First few rows of our data:
      office  delivery_time
0     North      23.856743
1     North      25.234891
2     North      22.987654
3     North      24.123456
4     North      23.567890`,
      explanation: "How to prepare your delivery time data for analysis"
    },
    {
      id: 3,
      title: "Running the Analysis",
      text: `Now we'll use ANOVA to check if delivery times are different between offices. This can be done by using the F-statistic and p-value from the ANOVA test. if p-value < 0.05 there is a difference.
And as we see in the output below, our fake data shows that there is a difference between offices.`,
      code: `# Group data by office
office_groups = [group for name, group in data.groupby('office')['delivery_time']]

# Run the ANOVA test
f_stat, p_value = stats.f_oneway(*office_groups)

print(f'Is there a difference between offices? (p-value < 0.05 means YES)')
print(f'p-value: {p_value:.4f}')
print(f'F-statistic: {f_stat:.4f}')`,
      output: `Is there a difference between offices? (p-value < 0.05 means YES)
p-value: 0.0237
F-statistic: 2.8534`,
      explanation: "How to compare delivery times between offices"
    },
    {
      id: 4,
      title: "Finding Specific Differences",
      text: `If ANOVA tells us there are differences, we need to find exactly which offices are different from each other. We use something called Tukey's test for this. It compares all pairs of offices and tells us where the differences are significant. 
In our example, we see that there is a significant difference between the North and the South offices. All other comparisons between offices are not significant. Therefor our point of action is to transfer knowledge from the North office to the South office, this is evident from below box plot's median values and will yield the highest returns.`,
      code: `from statsmodels.stats.multicomp import pairwise_tukeyhsd

# Run Tukey's test
tukey = pairwise_tukeyhsd(
    data['delivery_time'],
    data['office'],
    alpha=0.05
)

print("Differences between offices:")
print(tukey.summary())`,
      output: `Differences between offices:
Multiple Comparison of Means - Tukey HSD, FWER=0.05
=============================================
  group1   group2  meandiff  p-adj    lower    upper  reject
---------------------------------------------
  Central    East    -0.584  0.8961   -2.345   1.177  False
  Central   North    -1.234  0.2314   -2.995   0.527  False
  Central   South     0.789  0.6723   -0.972   2.550  False
  Central    West    -0.345  0.9672   -2.106   1.416  False
     East   North    -0.650  0.8456   -2.411   1.111  False
     East   South     1.373  0.1432   -0.388   3.134  False
     East    West     0.239  0.9889   -1.522   2.000  False
    North   South     2.023  0.0124    0.262   3.784   True
    North    West     0.889  0.5892   -0.872   2.650  False
    South    West    -1.134  0.3012   -2.895   0.627  False`,
      explanation: "The output shows that there is a significant difference between North and South offices (reject=True)"
    },
    {
      id: 5,
      title: "Visualizing Performance Differences",
      text: `Let's create a visualization to understand delivery time differences between offices. Note, I have just added the median values (you will have to run it yourself for the visual!) and since the code is artificially generated, I highly suggest you run it yourself in a Jupyter Notebook to spot difference and form your own strategic plan to address potential underperformers.`,
      code: `import matplotlib.pyplot as plt
import seaborn as sns

plt.figure(figsize=(10, 6))
sns.boxplot(x='office', y='delivery_time', data=data)
plt.title('Delivery Times by Office')
plt.xlabel('Office Location')
plt.ylabel('Delivery Time (hours)')
plt.axhline(y=24, color='r', linestyle='--', label='Target Time')
plt.legend()
plt.show()`,
      output: `[Box plot showing:
North office: median 22hrs, low variance
South office: median 26hrs, high variance
East office: median 23hrs, medium variance
West office: median 24hrs, low variance
Central office: median 25hrs, medium variance]`,
      explanation: "The graph shows that the North office consistently performs better with less variation in delivery times, especially when comparing it to the south office"
    },
    {
      id: 6,
  title: "Taking Action: A Strategic Approach",
  text: `Let's turn our findings into real improvements. Here's how we can make the South office as good as our best performer:


Step 1: Learn From The Best
Our North office is doing great work through two channels speed and cost:
1) They deliver in 22 hours (others take 26)
2 ) Each delivery costs them $42 (others spend $58)
Next step is to watch how they work and write down everything they do right.

Step 2: Understanding why good performance matters
If all offices worked like the North office, each delivery would be 4 hours faster and we would save $16 on every package. For 1,000 deliveries, that's $16,000 saved per office. This message needs to be communicated to the other offices to motivate them to imrpove through friendly competition. 
For rapid improvement, I would suggest to add a KPI based compensation to the officies that need to catch up, this can be done by being conservative in your cost savings and then giving the offices a percentage of the savings they make.

Step 3: Help the Other Offices Get Better  (South Office 12-Week Plan)

First Month:
• Show the South team how the North office plans routes by going through the routines and roles in the North Office.
• Get everyone using the same delivery planning tools as we want to standardize the process.
• Start tracking daily numbers to get a feel for how the new methods are working.

Next Two Months:
• Check if the new methods are working by having weekly meetings with the South team.
• Fix any problems we find
• Help teams adjust to the new way of working

Step 4: Keep Track of Progress
Watch these numbers daily as they are very important in implementation:
• How long deliveries take
• How much each delivery costs
• What customers say about our service
• How many deliveries arrive on time
• How much fuel we use

Step 5: Summary of the Project Plan in Numbers
What we needed to spend:
• $5,000 to train everyone
• $3,000 for better planning software
• $2,000 to write down all the best methods
  Total: $10,000

What the project will yield us in one year:
• Save $16,000 on delivery costs
• Deliver 4,000 more packages
• Make customers 15% happier (through less waiting)
That's $6,000 extra profit per office and higher performance across the board.

Step 6: The Project's Impact
When we're done, we'll have:
• All offices working smoothly the same way reducing key person risk
• Happier customers getting packages faster because of the new methods
• Lower costs on every delivery because of better planning
• Trucks and drivers being used better to the benefit of the environment
• An edge over our competitors because we can deliver faster and cheaper

This plan helps us deliver packages faster and cheaper, while making our customers happier. A ROI of 60% is a no-brainer and these opportunities exist internally in businesses many places but are not seized upon.`,
  explanation: "A clear roadmap for turning data insights into better business performance"
}
  ];

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedStates({ ...copiedStates, [id]: true });
    setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [id]: false }));
    }, 2000);
  };

  // Update the scrollToSection function to account for header height
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100; // Adjust this value based on your header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  // Add this function at the top of your component, after the sections array
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return (
    <div>
      <Layout />
      <div className="w-full min-h-screen bg-gradient-to-b from-white to-white text-black pt-20">
        <div className="max-w-screen-lg mx-auto p-4">
          <div className="mb-12 flex justify-center">
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm max-w-[600px] w-full">
              <h2 className="text-2xl font-bold mb-4 text-center">Table of Contents</h2>
              <div className="space-y-2">
                {sections.map(section => section.title && (
                  <div 
                    key={section.id} 
                    className="cursor-pointer hover:scale-110 transition-colors text-center"
                    
                    onClick={() => scrollToSection(`section-${section.id}`)}
                  >
                    {section.id}. {section.title}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-16">
            {sections.map(({ id, title, text, code, explanation }) => (
              <motion.div 
                key={id}
                id={`section-${id}`}  // Add this line
                initial={{ opacity: 1, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="flex flex-col gap-6"
              >
                <h2 className="text-3xl font-bold">{title}</h2>
                <div className="text-lg whitespace-pre-wrap">{text}</div>
                
                {code && (
                  <div className="relative">
                    <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto">
                      <code className="text-sm">{code}</code>
                    </pre>
                    <button
                      onClick={() => copyToClipboard(code, id)}
                      className="absolute top-2 right-2 p-2 bg-white rounded-md hover:bg-gray-200"
                    >
                      <FaCopy />
                      {copiedStates[id] && (
                        <span className="absolute -right-16 top-2 text-green-600">
                          Copied!
                        </span>
                      )}
                    </button>
                  </div>
                )}
                
                {explanation && (
                  <p className="text-gray-600 italic">{explanation}</p>
                )}
              </motion.div>
            ))}
          </div>
          {/* Back to Top Button */}
          <div className="flex justify-center mt-12 mb-8">
            <button
              onClick={scrollToTop}
              className=" text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors duration-300 hover:scale-110" 
              style={{ background: 'rgb(1, 50, 32)' } }>
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AArticleAutoVarianceAnalysis;